import type { DataChain } from './types.ts'

/**
 * `{{ path }}` rendering over a node's options — a runtime pre-step, not a node
 * (ADR-0007 §6, study §6).
 *
 * Directus's two grammars are copied verbatim because they are the useful part:
 * a string that is *exactly* one mustache keeps the value's type, so
 * `{{ $lead.price }}` reaches a number field as a number; a mustache inside a
 * longer string interpolates, and an object interpolated that way is
 * JSON-stringified rather than rendered `[object Object]`. Paths are get-only —
 * dot and `[i]`, no helpers, no calls, nothing that could reach outside the
 * chain it was handed.
 */

const WHOLE = /^\s*\{\{\s*([^{}]+?)\s*\}\}\s*$/
const EMBEDDED = /\{\{\s*([^{}]+?)\s*\}\}/g

/** A canonical array index: `0`, `7`; never `01`, and never `length`. */
const INDEX = /^(0|[1-9][0-9]*)$/

/** Read one get-only path out of a value. `undefined` when it does not resolve. */
export function resolvePath(root: unknown, path: string): unknown {
  const segments = splitPath(path)
  if (segments === null) return undefined

  let cursor: unknown = root
  for (const segment of segments) {
    if (cursor === null || cursor === undefined) return undefined
    if (typeof cursor !== 'object') return undefined
    // An **index** reaches into an array; a name reaches into an object.
    // Nothing else: a path may not call, and may not walk the prototype chain.
    // `length` is an array's own property, so `hasOwnProperty` alone would let
    // `{{ $lead.tags.length }}` render `2` — a read outside the declared
    // grammar, and one the validator has no way to check.
    if (Array.isArray(cursor) && !INDEX.test(segment)) return undefined
    if (!Object.prototype.hasOwnProperty.call(cursor, segment)) return undefined
    cursor = (cursor as Record<string, unknown>)[segment]
  }
  return cursor
}

/**
 * Is this string *exactly* one `{{ path }}`?
 *
 * The question matters wherever a rendered value's **type** is part of the
 * contract: only this grammar preserves it, while an embedded mustache always
 * renders a string. A `number` field declared templatable accepts this shape
 * and no other.
 */
export const isWholeTemplate = (text: string): boolean => WHOLE.test(text)

/**
 * `a.b[0].c` → `['a','b','0','c']`, or `null` for a path this grammar does not
 * describe. Splitting is shared with the validator so a path that saves is a
 * path that resolves.
 */
export function splitPath(path: string): string[] | null {
  const trimmed = path.trim()
  if (trimmed === '') return null

  const segments: string[] = []
  for (const part of trimmed.split('.')) {
    // A segment is a *name*: a chain root, a node key, a field, or an id. The
    // set is deliberately narrower than "anything but brackets" so that a path
    // which cannot name anything is refused at save rather than resolving to
    // `undefined` at run time.
    const match = /^([A-Za-z0-9_$-]+)((?:\[\d+\])*)$/.exec(part)
    if (!match) return null
    segments.push(match[1])
    for (const index of match[2].matchAll(/\[(\d+)\]/g)) segments.push(index[1])
  }
  return segments
}

/** The root a path starts at: `$lead` for `$lead.name`, `stage` for `stage.id`. */
export function pathRoot(path: string): string | null {
  const segments = splitPath(path)
  return segments === null ? null : segments[0]
}

/**
 * Every `{{ path }}` a value names, at any depth, in document order.
 *
 * `validateFlowGraph` walks these to check that each one names something the
 * chain will actually hold; the console's data-chain picker offers the same set
 * in reverse.
 */
export function templatePathsIn(value: unknown): string[] {
  const found: string[] = []
  walk(value, (text) => {
    for (const match of text.matchAll(EMBEDDED)) found.push(match[1].trim())
  })
  return found
}

function walk(value: unknown, onString: (text: string) => void): void {
  if (typeof value === 'string') {
    onString(value)
    return
  }
  if (Array.isArray(value)) {
    for (const item of value) walk(item, onString)
    return
  }
  if (typeof value === 'object' && value !== null) {
    for (const item of Object.values(value)) walk(item, onString)
  }
}

/**
 * Render every template in a node's options against the chain.
 *
 * `undefined` becomes `null` on the way out, everywhere, because the rendered
 * options are what the step log stores and JSON has no `undefined` — a value
 * that vanished on serialisation would make the log disagree with what ran.
 *
 * **A path that does not resolve renders as the empty string inside a longer
 * string, and as `null` on its own.** This is the one place Directus is not
 * copied: it renders the literal text `undefined` (study §6), which would put
 * a JavaScript token into a lead name a customer eventually reads. The louder
 * behaviour has a home already — `validateFlowGraph` refuses a template naming
 * a path the chain cannot hold, at save, where a person can fix it.
 */
export function renderOptions(
  options: Record<string, unknown>,
  chain: DataChain,
): Record<string, unknown> {
  const rendered: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(options)) {
    rendered[key] = renderValue(value, chain)
  }
  return rendered
}

/** {@link renderOptions} for one value — exported for the runtime's own use. */
export function renderValue(value: unknown, chain: DataChain): unknown {
  if (typeof value === 'string') return renderString(value, chain)
  if (Array.isArray(value)) return value.map((item) => renderValue(item, chain))
  if (typeof value === 'object' && value !== null) {
    return renderOptions(value as Record<string, unknown>, chain)
  }
  return value === undefined ? null : value
}

function renderString(text: string, chain: DataChain): unknown {
  const whole = WHOLE.exec(text)
  if (whole) {
    const value = resolvePath(chain, whole[1])
    return value === undefined ? null : value
  }
  return text.replace(EMBEDDED, (_match, path: string) =>
    stringify(resolvePath(chain, path.trim())),
  )
}

function stringify(value: unknown): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') return JSON.stringify(value) ?? ''
  return String(value)
}
